/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useEffect, useRef } from 'react';

import { Button, Typography2 } from '@coursera/cds-core';
import { CheckIcon } from '@coursera/cds-icons';

import _t from 'i18n!nls/expression-of-interest';

type Props = {
  firstName: string;
  programName?: string;
  onStartApplication?: () => void;
  onBookCall?: () => void;
};

const styles = {
  heading: css`
    margin-bottom: 0;
  `,
  whatsNext: css`
    margin: var(--cds-spacing-200) 0 var(--cds-spacing-150);
  `,
  list: css`
    list-style: none;
    margin: 0 0 var(--cds-spacing-200);
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-150);
  `,
  listItem: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-100);
  `,
  iconBadge: css`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: var(--cds-color-feedback-success);
  `,
  // Grayed checkmark for checklist steps the learner hasn't completed yet.
  iconBadgePending: css`
    background: var(--cds-color-emphasis-neutral-background-default);
  `,
  actions: css`
    display: flex;
    gap: var(--cds-spacing-150);
    flex-wrap: wrap;
  `,
};

const SurveyTerminalScreen = ({ firstName, programName, onStartApplication, onBookCall }: Props) => {
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const checklistItems = [
    _t('Check your inbox for program details and application checklist'),
    _t('Schedule a call with an enrollment counselor'),
    _t("Start your application, we'll guide you step-by-step"),
  ];

  return (
    <div data-testid="survey-terminal-screen">
      <Typography2 component="h3" variant="titleSmall" css={styles.heading} ref={headingRef} tabIndex={-1}>
        {programName
          ? _t("You're one step closer to the #{programName}, #{firstName}", { programName, firstName })
          : _t("You're one step closer, #{firstName}", { firstName })}
      </Typography2>
      <Typography2 component="p" variant="bodyPrimary" css={styles.whatsNext}>
        {_t("What's next:")}
      </Typography2>
      <ul css={styles.list}>
        {checklistItems.map((item, index) => (
          <li key={item} css={styles.listItem}>
            <span css={[styles.iconBadge, index > 0 && styles.iconBadgePending]} aria-hidden="true">
              <CheckIcon size="small" color="invert" />
            </span>
            <Typography2 component="span" variant="bodyPrimary">
              {item}
            </Typography2>
          </li>
        ))}
      </ul>
      {(onStartApplication || onBookCall) && (
        <div css={styles.actions}>
          {onStartApplication && (
            <Button variant="primary" onClick={onStartApplication}>
              {_t('Start application')}
            </Button>
          )}
          {onBookCall && (
            <Button variant="secondary" onClick={onBookCall}>
              {_t('Book a call')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default SurveyTerminalScreen;
